import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ArrowLeft, TextAUnderline } from "@phosphor-icons/react";
import duasSections from "../../data/duasSections.json";
import PageTitle from "../../components/ui/PageTitle";
import { slugify } from "./utils";

type DuaItem = { title: string; header?: string; text?: string; text2?: string };
type DuaParent = { title: string; duas?: DuaItem[] };
type Section = { title: string; items: DuaParent[] };

const FONT_KEY = "dua_detail_font_size";
const DEFAULT_SIZE = 34;

export default function DuaDetailPage() {
  const { parentSlug = "", duaSlug = "" } = useParams();
  const navigate = useNavigate();

  const dua = useMemo(() => {
    for (const s of duasSections as Section[])
      for (const it of s.items)
        if (slugify(it.title) === parentSlug)
          return it.duas?.find((d) => slugify(d.title) === duaSlug);
  }, [parentSlug, duaSlug]);

  const [fontSize, setFontSize] = useState<number>(DEFAULT_SIZE);
  useEffect(() => {
    const saved = localStorage.getItem(FONT_KEY);
    if (saved) setFontSize(Number(saved));
  }, []);
  const onChange = (v: number) => { setFontSize(v); localStorage.setItem(FONT_KEY, String(v)); };

  return (
    <Container>
      <TopBar>
        <Back onClick={() => navigate(-1)} aria-label="رجوع">
          <ArrowLeft size={18} weight="bold" color="#fff" />
        </Back>
        <PageTitle center icon={<TextAUnderline size={24} color="var(--primary)" weight="duotone" />}>
          {dua?.title ?? "الدعاء"}
        </PageTitle>
      </TopBar>

      <Card>
        <ControlBar>
          <Label>حجم الخط</Label>
          <Range min={20} max={60} step={1} value={fontSize} onChange={(e) => onChange(Number(e.target.value))} />
          <SizeBadge>{fontSize}</SizeBadge>
        </ControlBar>

        <Body>
          {dua?.header && <HeaderText>{dua.header}</HeaderText>}
          <Basmala>﷽</Basmala>
          <DuaText $size={fontSize}>{dua?.text ?? "لم يتم العثور على نص الدعاء."}</DuaText>
          {dua?.text2 && <DuaText $size={fontSize}>{dua.text2}</DuaText>}
        </Body>
      </Card>
    </Container>
  );
}

/* styles */
const Container = styled.div` padding:24px 16px; max-width:960px; margin:0 auto; `;
const TopBar = styled.div` display:flex; align-items:center; gap:12px; justify-content:center; position:relative; `;
const Back = styled.button`
  position:absolute; inset-inline-start:0; top:0;
  width:40px; height:40px; border-radius:20px; border:none; cursor:pointer;
  background: linear-gradient(135deg, var(--primary), var(--border));
  display:grid; place-items:center; box-shadow: 0 6px 12px rgba(0,0,0,.15);
`;
const Card = styled.div`
  background: var(--surface); border-radius: 16px; box-shadow: var(--shadow);
  padding: 14px; margin-top: 12px;
`;
const ControlBar = styled.div`
  display:grid; grid-template-columns:auto 1fr auto; gap:10px; align-items:center;
  background: var(--surface); border:1px solid var(--border); border-radius:12px; padding:8px 12px;
`;
const Label = styled.span` color:var(--primary); font-weight:600; font-size:.95rem; `;
const Range = styled.input.attrs({ type: "range" })` width:100%; accent-color: var(--primary); `;
const SizeBadge = styled.span` min-width:40px; text-align:center; color:var(--primary); font-weight:700; `;
const Body = styled.div` margin-top:12px; border-radius:16px; overflow:hidden; `;
const HeaderText = styled.h4` margin:8px 0 4px; text-align:center; color:var(--primary); font-weight:500; font-size:1.6rem; `;
const Basmala = styled.div`
  text-align:center; color:var(--primary);
  font-size: clamp(2rem, 6vw, 3.2rem);
  font-family: "Lateef","Scheherazade",system-ui;
  margin: 6px 0 12px;
`;
const DuaText = styled.p<{ $size:number }>`
  direction: rtl; text-align: center; color: var(--text);
  font-family: "Lateef","Scheherazade",system-ui;
  font-size: ${(p)=>p.$size}px; line-height: ${(p)=>Math.round(p.$size*1.5)}px;
  margin: 10px 0; white-space: pre-wrap; transition: font-size .16s ease, line-height .16s ease;
`;
